import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  Link,
  TextField,
} from "@mui/material";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import Loader from "../../components/loader/loader";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch } from "react-redux";
import { sendDispositionMailApi } from "../../API/sendDispositionMail";
import SendAPBImageList from "../../components/sendAPBImageList/sendAPBImageList";
import { IOsdDisposition } from "../../models/osd";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { getDispositionData } from "../../API/getDispositionData";
import CustomDialog from "../../components/confirmationPopup/confirmationPopup";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import { useApiHeaders } from "../../@hooks/useApiHeaders";
import { M_GRID_PROPS } from "../../constants";

// Helper functions
const formatAddress = ({
  address1,
  address2,
  city,
  state,
  zip,
}: {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}) => {
  const line1 = [address1, address2].filter(Boolean).join(" ");
  const line2 = [city, state, zip].filter(Boolean).join(", ");
  return [line1, line2].filter(Boolean).join(", ");
};

const convertImageToBase64 = async (url: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const generateEmailContent = async (osdDetails: IOsdDisposition) => {
  const logoBase64 = await convertImageToBase64(
    `${process.env.PUBLIC_URL}/Saia_ltl.png`
  );

  const fields = [
    { label: "SAIA PRO", value: osdDetails.proNumber },
    { label: "PU DATE", value: osdDetails.pickupDate },
    { label: "CONSIGNEE", value: osdDetails.consigneeName },
    {
      label: "ADDRESS",
      value: formatAddress({
        address1: osdDetails.consigneeAddress1,
        address2: osdDetails.consigneeAddress2,
        city: osdDetails.consigneeCity,
        state: osdDetails.consigneeState,
        zip: osdDetails.consigneeZip,
      }),
    },
    { label: "SHIPPER", value: osdDetails.shipperName },
    {
      label: "ADDRESS",
      value: formatAddress({
        address1: osdDetails.shipperAddress1,
        address2: osdDetails.shipperAddress2,
        city: osdDetails.shipperCity,
        state: osdDetails.shipperState,
        zip: osdDetails.shipperZip,
      }),
    },
    { label: "PIECES", value: osdDetails.pieces },
    { label: "WEIGHT", value: osdDetails.weight },
    { label: "PO#", value: osdDetails.poNumber },
    { label: "BOL#", value: osdDetails.blNumber },
    { label: "SN#", value: osdDetails.snNumber },
    { label: "TYPE OF REFUSAL", value: osdDetails.reasonForRefusal },
  ];

  const fieldLines = fields
    .map(({ label, value }) => `<p>${label}: ${value ?? ""}</p>`)
    .join("\n");

  return `
    <img src="${logoBase64}" alt="Logo" />
    <p>&nbsp;</p>
    <p> Hello, </p> 
    <p>We would like to inform you that the following shipment has been refused on ${osdDetails.dateOfRefusal}
    and is being returned to the terminal for the reasons outlined below: </p> 
    <p>&nbsp;</p>
    ${fieldLines}
    <p>REASON FOR REFUSAL:  </p>
    <p>PRODUCT ACCEPTED BY CONSIGNEE [Yes/No]: </p>
    <p>&nbsp;</p>
    <p>We kindly request that you provide the value and instructions for the return of the shipment to 
    the shipper, or guidance on the disposition of the order. </p> 
    <p>Enclosed, please find several documents that may prove helpful, including the Bill of Lading, 
    Delivery Receipt, and photographs of the product.</p> 
    <p>Thank you for your attention to this matter.</p>`;
};


const SendDisposition: React.FC = () => {
  const { osdNumber } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [osdDetails, setOsdDetails] = useState<IOsdDisposition | null>(null);
  const [html, setHtml] = useState<string>(""); // Start with an empty string
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const subject: string = `Requesting Disposition PRO: ${osdDetails?.proNumber ?? ""}`;

  const apiHeaders = useApiHeaders({ sectionName: "DispositionMail", osdType: osdDetails?.osdType, osdNumber: osdNumber});

  const showNotification = useCallback((type: NOTIFICATION_TYPE, message: string) => {
    dispatch(PopoutNotification(type, message, Date.now().toString()));
  }, [dispatch]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDialogOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback((flag?: string) => {
    setDialogOpen(false);
    if (flag === "yes") {
      navigateBack();
    }
  }, [navigateBack]);

  const handleImageSelect = useCallback((imageUrl: string) => {
    setSelectedImages((prevSelectedImages) => {
      const updatedImages = prevSelectedImages.includes(imageUrl)
        ? prevSelectedImages.filter((url) => url !== imageUrl)
        : [...prevSelectedImages, imageUrl];
      return updatedImages;
    });
  }, []);

  const getExceptionDetails = useCallback(async () => {
    try {
      const data = await getDispositionData(osdNumber + "", apiHeaders);
      setOsdDetails(data);
    } catch (error) {
      console.error("Error fetching disposition data:", error);
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdNumber]);

  useEffect(() => {
    getExceptionDetails();
  }, [getExceptionDetails]);

  const validateEmail = useCallback((emails: string) => {
    if (!emails.trim()) {
      setError("Email is required");
      return false;
    }

    if (emails.length > 900) {
      setError("Email address length should not exceed 900 characters");
      return false;
    }

    const safeEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValid = emails
      .split(",")
      .map((email) => email.trim())
      .every((email) => safeEmailRegex.test(email));

    if (!isValid) {
      setError("Invalid email address");
      return false;
    }

    setError("");
    return true;
  }, []);

  useEffect(() => {
    if (!osdDetails) return;

    generateEmailContent(osdDetails)
      .then(setHtml)
      .catch((error) => console.error("Error generating email content:", error));
  }, [osdDetails]);

  const handleSend = useCallback(async () => {
    if (!validateEmail(to)) return;

    const params = {
      to,
      subject,
      html,
      attachmentImageUrls: selectedImages,
      emailType: "Disposition",
    };

    setLoading(true);
    try {
      await sendDispositionMailApi(osdNumber?.toString(), params, [], apiHeaders);
      showNotification(NOTIFICATION_TYPE.SUCCESS, "Email sent successfully");
      navigateBack();
    } catch (error) {
      console.error("Error sending mail:", error);
      showNotification(NOTIFICATION_TYPE.ERROR, "Error sending mail");
    } finally {
      setLoading(false);
    }
  }, [to, subject, html, selectedImages, osdNumber, validateEmail, showNotification, navigateBack, apiHeaders]);

  return (
    <div className="bg-layout">
      {/* header */}
      <div className="header-section">
        <div className="back-section">
          <button type="button"
            data-testid="back-button"
            onClick={handleDialogOpen}
            className="back"
          >
            <Link>
              <ArrowLeftIcon />
            </Link>
          </button>
          <div className="page-header-content">
            <div className="page-header"> &nbsp;&nbsp;Send Disposition</div>
          </div>
        </div>
        <div className="edit-section">
          <Button
            variant="contained"
            data-testid="send-button"
            className="assign"
            onClick={handleSend}
          >
            Send
          </Button>
        </div>
      </div>

      {/* body */}
      <div className="content-area osd-description">
        <Grid container spacing={2} className="filter-margin padding-15 mb-0">
          <Grid item {...M_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={{ fontSize: "13px" }}
                shrink={true}
                className="label-email"
                htmlFor="to"
              >
                To<sup>*</sup>{" "}
                <TooltipWithClickAway
                  tooltipText="Enter multiple email addresses, separated by commas."
                  tooltipKey="tooltip2"
                  data-testid="tooltip-to"
                />
              </InputLabel>
              <TextField
                autoComplete="off"
                id="to"
                name="to"
                variant="outlined"
                value={to}

                onChange={(e) => {
                  setTo(e.target.value);
                  setError("");
                }}
                inputProps={{ maxLength: 900 }}
                error={Boolean(error)}
                helperText={error}
              />
            </FormControl>
          </Grid>

          <Grid item {...M_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={{ fontSize: "13px" }}
                className="m-20"
                shrink={true}
              >
                Subject<sup>*</sup>
              </InputLabel>
              <TextField
                autoComplete="off"
                name="subject"
                variant="outlined"
                value={subject}
                inputProps={{ maxLength: 100 }}
                disabled
              />
            </FormControl>
          </Grid>
        </Grid>

        <SendAPBImageList
          osdNumber={+(osdNumber + "")}
          setSelectedImages={setSelectedImages}
          selectedImages={selectedImages}
          handleImageSelect={handleImageSelect}
          exceptionType={osdDetails?.osdType}
          isDisposition={true}
        />

        <ReactQuill
          className="padding-15 pt-0"
          theme="snow"
          value={html}
          data-testid="quill-editor"
          onChange={setHtml}
        />
      </div>
      <Loader pageLoader={loading} />

      <CustomDialog
        message={
          "Email will not be sent! Are you sure you want to exit this page?"
        }
        open={dialogOpen}
        onClose={handleDialogClose}
      />
    </div>
  );
};

export default SendDisposition;
